import { RefObject, useEffect, useState } from 'react';

/**
 * Clamp the height of a panel that floats ABOVE the search bar so it never overlaps the sticky top
 * nav. The panel's bottom is anchored at the search bar's top edge (absolute `bottom-full` in the
 * parent); we measure that bottom each scroll/resize tick and subtract the nav height to get the
 * available space above it.
 *
 * Shared by the discussion thread (DiscussionThread `panel`) and the floating preamble/question
 * banner (AboveBarPromptPanel) so both clip the same way.
 *
 * The clamp only tightens on a real layout/resize change. Page scroll may relax it but never
 * squeeze it below the content's own height — see the note in `compute`.
 *
 * @param ref     the panel element whose `getBoundingClientRect().bottom` marks the search bar's top
 * @param enabled only measure while the panel is mounted/visible
 * @returns the max-height in px, or `null` until first measured / when disabled
 */
export function useAboveBarMaxHeight<T extends HTMLElement>(
  ref: RefObject<T>,
  enabled: boolean,
): number | null {
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const compute = (fromScroll = false) => {
      const el = ref.current;
      if (!el) return;
      // el.getBoundingClientRect().bottom == viewport-y of the search bar's top edge
      const panelBottom = el.getBoundingClientRect().bottom;
      // Find the sticky top nav by picking the first .sticky.top-0 element that is
      // actually at the top of the viewport; fall back to a safe 64px default.
      const stickyEl = document.querySelector<HTMLElement>('.sticky.top-0');
      const stickyRect = stickyEl?.getBoundingClientRect();
      const navBottom = stickyRect && stickyRect.top <= 4 ? stickyRect.bottom : 64;
      const gap = 12;
      const available = Math.max(panelBottom - navBottom - gap, 120);
      // Page scroll drags the search bar — and so this panel's bottom — toward the nav, which used
      // to tighten the clamp on every tick: scrolling down slowly ate the panel from the top and
      // turned it into a scroller on the way out, instead of letting it scroll away under the nav
      // like ordinary page content. So scroll must never clamp tighter than the content actually
      // needs (`scrollHeight`, +1 for its integer rounding); only a genuine layout/resize change
      // — which is where a real nav collision comes from — re-baselines downward. When the content
      // already fits, this converges the clamp to the content height and no scroller ever appears;
      // when it was already clipped at rest, the resting clamp simply holds.
      setMaxHeight((prev) =>
        fromScroll && prev != null ? Math.max(available, Math.min(prev, el.scrollHeight + 1)) : available,
      );
    };

    const measure = () => compute();
    const onScroll = () => compute(true);

    const raf = requestAnimationFrame(measure);
    // The mount-time measure can be skewed: the panel mounts mid `animate-fade-in-from-below`
    // (translateY shifts getBoundingClientRect), and the page often relayouts right around a
    // submit (hero collapse, results shelf mounting) which moves the search bar WITHOUT firing
    // any scroll/resize event. A stale too-small clamp here is what used to clip the question
    // until the next user scroll. Re-measure once the mount animation is over, and on any page
    // layout change via a body ResizeObserver.
    const settle = setTimeout(measure, 300);
    const layoutObserver = new ResizeObserver(measure);
    layoutObserver.observe(document.body);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      layoutObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
    };
  }, [enabled, ref]);

  return maxHeight;
}
